<template>
    <div>
        <h2>Settings</h2>
        <Spinner v-if="displaySpinner" />
        <Tabs :value="activeTab">
            <TabList>
                <Tab v-for="tab in tabs" :key="tab.value" :value="tab.value">
                    <router-link
                        :to="tab.route"
                        custom
                        v-slot="{ href, navigate }"
                    >
                        <a :href="href" @click="navigate" class="tab-link">
                            {{ tab.label }}
                        </a>
                    </router-link>
                </Tab>
            </TabList>
            <TabPanels>
                <TabPanel :value="SettingsTab.GENERAL">
                    <div class="general-section">
                        <p>General information about your profile.</p>
                        <div class="form-field">
                            <label for="fullname">Name</label>
                            <InputText
                                id="fullname"
                                type="text"
                                v-model="genForm.fullname"
                            />
                        </div>
                        <div class="form-field">
                            <label for="email">Email</label>
                            <InputText
                                id="email"
                                type="text"
                                v-model="genForm.email"
                                disabled
                            />
                        </div>
                        <div class="button-group">
                            <Button
                                label="Cancel"
                                severity="secondary"
                                @click="cancelProfileEdit"
                            />
                            <Button
                                label="Update Changes"
                                :loading="isSavingProfile"
                                @click="saveProfile"
                            />
                        </div>
                    </div>

                    <div class="general-section hints-section">
                        <h3>Hints</h3>
                        <p>
                            Control whether onboarding hints are shown on pages.
                        </p>
                        <div class="hints-controls">
                            <div class="hints-toggle">
                                <label for="hints-enabled">Show hints</label>
                                <ToggleSwitch
                                    id="hints-enabled"
                                    v-model="hintsEnabledLocal"
                                    @change="onHintsEnabledChange"
                                />
                            </div>
                            <Button
                                label="Reset hints"
                                severity="secondary"
                                size="small"
                                variant="link"
                                @click="onResetHints"
                                v-tooltip="
                                    'Resets all dismissed hints so they show again'
                                "
                            />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel :value="SettingsTab.ORGANIZATION">
                    <div class="general-section">
                        <div class="roles-header">
                            <h3>Organizations &amp; Roles</h3>
                            <Button
                                label="Select Organization"
                                icon="pi pi-sync"
                                @click="
                                    router.push({ name: 'organizationPage' })
                                "
                                v-if="organizations && organizations.length > 1"
                                severity="secondary"
                            />
                        </div>
                        <p>
                            A list of all organizations you have access to and
                            which role you have for them.
                        </p>
                        <div
                            v-if="
                                !user?.roles ||
                                Object.keys(user.roles).length === 0
                            "
                            class="empty-roles"
                        >
                            You don't have any roles assigned yet.
                        </div>
                        <DataTable
                            v-else
                            :value="formattedRoles"
                            class="roles-table"
                            stripedRows
                        >
                            <Column
                                field="organization"
                                header="Organization"
                                sortable
                            >
                                <template #body="slotProps">
                                    <div class="org-name-cell">
                                        <span>
                                            {{ slotProps.data.organization }}
                                        </span>
                                    </div>
                                </template>
                            </Column>
                            <Column field="roles" header="Role">
                                <template #body="slotProps">
                                    <div class="role-badges">
                                        <span
                                            v-for="role in displayRoles(
                                                slotProps.data.roles
                                            )"
                                            :key="role.value"
                                            class="role-badge"
                                            :class="roleClass(role.value)"
                                        >
                                            {{ role.label }}
                                        </span>
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </TabPanel>

                <TabPanel :value="SettingsTab.SECURITY">
                    <Button
                        class="p-button-link"
                        label="Sign out"
                        @click="signOutUser"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Change password"
                        @click="initiatePasswordReset"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Reset MFA"
                        :disabled="!authUser.mfaEnabled"
                        @click="() => resetMfa()"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Control account access"
                        :disabled="true"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Download personal data"
                        :disabled="true"
                    />
                    <br />
                    <div class="delete-account" style="float: right">
                        <Button
                            class="p-button-danger"
                            label="Delete Account"
                            :disabled="true"
                        />
                    </div>
                </TabPanel>

                <TabPanel :value="SettingsTab.API_KEYS">
                    <div class="api-keys-section">
                        <h3>Your API Keys</h3>
                        <p>
                            API keys allow external applications to access the
                            API on your behalf.
                        </p>

                        <div class="api-key-actions">
                            <Button
                                label="Create New API Key"
                                icon="pi pi-plus"
                                @click="apiKeyStore.openCreateApiKeyDialog()"
                                class="p-button-primary mr-2"
                            />
                        </div>

                        <div
                            v-if="isLoading && apiKeys.length === 0"
                            class="loading-container"
                        >
                            <i
                                class="pi pi-spin pi-spinner"
                                style="font-size: 2rem"
                            ></i>
                            <p>Loading your API keys...</p>
                        </div>

                        <div v-else-if="error" class="error-container">
                            <i
                                class="pi pi-exclamation-triangle"
                                style="font-size: 1.5rem; color: #f44336"
                            ></i>
                            <p>{{ error }}</p>
                            <Button
                                label="Try Again"
                                @click="retryLoading"
                                class="p-button-sm mt-2"
                            />
                        </div>

                        <DataTable
                            v-else
                            :value="apiKeys"
                            :loading="isLoading"
                            class="api-keys-table"
                            :rows="10"
                            paginator
                            :emptyMessage="'No API keys found. Create one above.'"
                        >
                            <template #empty>
                                <p>
                                    No API keys found. Create your first API key
                                    using the form above.
                                </p>
                            </template>
                            <Column field="name" header="Name" sortable />
                            <Column
                                field="created_at"
                                header="Created"
                                sortable
                            >
                                <template #body="slotProps">
                                    {{ formatDate(slotProps.data.created_at) }}
                                </template>
                            </Column>
                            <Column
                                field="expires_at"
                                header="Expires"
                                sortable
                            >
                                <template #body="slotProps">
                                    {{
                                        slotProps.data.expires_at
                                            ? formatDate(
                                                  slotProps.data.expires_at
                                              )
                                            : "Never"
                                    }}
                                </template>
                            </Column>
                            <Column
                                field="last_used_at"
                                header="Last Used"
                                sortable
                            >
                                <template #body="slotProps">
                                    {{
                                        slotProps.data.last_used_at
                                            ? formatDate(
                                                  slotProps.data.last_used_at
                                              )
                                            : "Never"
                                    }}
                                </template>
                            </Column>
                            <Column header="Actions" style="width: 120px">
                                <template #body="slotProps">
                                    <Button
                                        severity="danger"
                                        text
                                        @click="
                                            (e) =>
                                                revokeApiKey(e, slotProps.data)
                                        "
                                        label="Revoke"
                                    />
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </TabPanel>
            </TabPanels>
        </Tabs>
    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import ToggleSwitch from "primevue/toggleswitch";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";

import { useServices } from "@/lib/openapi";
import Spinner from "@/shared/Spinner.vue";
import {
    type ApiKey,
    useApiKeyManager,
    useApiKeyStore,
} from "@/stores/api-key-store";
import {
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { ClusterRole, useUserStore } from "@/stores/user-store";

const toast = useToast();
const router = useRouter();
const route = useRoute();
const apiKeyStore = useApiKeyStore();
const { apiKeys, isLoading, error } = useApiKeyManager();
const { signOut, resetOtp, sendPasswordResetEmail } = useAuthenticationStore();
const { authUser } = useAuthenticationManager();
const userStore = useUserStore();
const { user, hintsEnabled } = storeToRefs(userStore);
const { reset: resetUser, resetHints, setHintsEnabled } = userStore;
const { organizations } = useOrganizationsManager();
const displaySpinner = ref(false);
const isSavingProfile = ref(false);
// hintsEnabled is a computed ref from the store; storeToRefs returns it reactive
const hintsEnabledLocal = ref<boolean>(hintsEnabled.value);
const genForm = ref({
    fullname: "",
    email: "",
});

const api = useServices({
    opts: { throwOnError: false },
    requires: { auth: true, organization: false, stek: false },
});

enum SettingsTab {
    GENERAL = "general",
    ORGANIZATION = "organization",
    SECURITY = "security",
    API_KEYS = "api-keys",
}

// Tab management with routing
const tabs = [
    {
        value: SettingsTab.GENERAL,
        label: "General",
        route: { name: "settings-general" },
    },
    {
        value: SettingsTab.ORGANIZATION,
        label: "Organizations & Roles",
        route: { name: "settings-organizations" },
    },
    {
        value: SettingsTab.SECURITY,
        label: "Security",
        route: { name: "settings-security" },
    },
    {
        value: SettingsTab.API_KEYS,
        label: "API Keys",
        route: { name: "settings-api-keys" },
    },
];

// Computed property to get the active tab from the route
const activeTab = computed(() => {
    const tab = tabs.find((tab) => {
        return tab.route.name === route.name;
    });
    return tab?.value || SettingsTab.GENERAL;
});

watch(activeTab, console.log, { immediate: true });

// Navigate to the correct tab based on the route when component is mounted
onMounted(() => {
    if (route.name === "settings") {
        router.replace({ name: "settings-general" });
    }
});

const ROLE_LABELS: Record<ClusterRole, string> = {
    [ClusterRole.USER]: "Editor",
    [ClusterRole.STEK_MANAGER]: "Stek Manager",
    [ClusterRole.USER_MANAGER]: "User Manager",
    [ClusterRole.ORGANIZATION_MANAGER]: "Organization Manager",
    [ClusterRole.VIEWER]: "Viewer",
};

const roleClass = (role: ClusterRole) => ({
    "role-manager":
        role === ClusterRole.USER_MANAGER ||
        role === ClusterRole.ORGANIZATION_MANAGER,
    "role-stek-manager": role === ClusterRole.STEK_MANAGER,
    "role-editor": role === ClusterRole.USER,
    "role-viewer": role === ClusterRole.VIEWER,
});

/**
 * Returns the roles to display for a user. VIEWER is only shown when it is
 * the only role (i.e. the user has no other permissions).
 */
const displayRoles = (roles: ClusterRole[]) => {
    const nonViewer = roles.filter((r) => r !== ClusterRole.VIEWER);
    const toShow = nonViewer.length > 0 ? nonViewer : [ClusterRole.VIEWER];
    return toShow.map((r) => ({ value: r, label: ROLE_LABELS[r] ?? r }));
};

// Format user roles for the DataTable
const formattedRoles = computed(() => {
    if (!user.value?.roles) return [];

    return Object.entries(user.value.roles).map(([orgId, roles]) => {
        // Find organization name if available
        const org = organizations.value?.find((o) => o.identity === orgId);

        return {
            organization: org?.name || orgId,
            orgId: orgId,
            roles: roles,
        };
    });
});

// Retry loading API keys
const retryLoading = async () => {
    await apiKeyStore.refresh();
};

const confirm = useConfirm();

const revokeApiKey = async (event: Event, key: ApiKey) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure? This action cannot be undone.",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Revoke",
            severity: "danger",
        },
        accept: async () => {
            const success = await apiKeyStore.revokeApiKey(key.identity);
            if (success) {
                toast.add({
                    severity: "success",
                    summary: "API key revoked",
                    detail: `The API key "${key.name}" has been revoked successfully.`,
                    life: 3000,
                });
            }
        },
    });
};

const formatDate = (dateStr?: Date | string | null): string => {
    if (!dateStr) return "Never";
    if (typeof dateStr === "string") {
        return new Date(dateStr).toLocaleString();
    } else {
        return dateStr.toLocaleString();
    }
};

const cancelProfileEdit = () => {
    if (user.value) {
        genForm.value.fullname = user.value.name;
    }
};

const saveProfile = async () => {
    isSavingProfile.value = true;
    try {
        const { response } = await api.updateSelfUserMePut({
            body: { name: genForm.value.fullname },
        });
        if (response?.ok) {
            toast.add({
                severity: "success",
                summary: "Profile updated",
                life: 3000,
            });
        } else {
            toast.add({
                severity: "error",
                summary: "Failed to update profile",
                life: 3000,
            });
        }
    } finally {
        isSavingProfile.value = false;
    }
};

const onHintsEnabledChange = async () => {
    await setHintsEnabled(hintsEnabledLocal.value);
};

const onResetHints = async () => {
    await resetHints();
    toast.add({
        severity: "success",
        summary: "Hints reset",
        detail: "All hints will show again on their respective pages.",
        life: 3000,
    });
};

const signOutUser = async () => {
    await signOut({
        severity: "success",
        summary: "Succesfully logged out.",
        life: 5000,
    });
    router.push({ name: "home" });
    resetUser();
};

const resetMfa = async () => {
    try {
        await resetOtp();
        toast.add({
            severity: "success",
            summary:
                "Succesfully set up multi-factor authentication with Authenticator.",
        });
    } catch {
        toast.add({
            severity: "error",
            summary:
                "Failed to register an authenticator app. You cannot login until it's properly setup.",
        });
    }
};

const initiatePasswordReset = async () => {
    try {
        if (user.value?.email) {
            const success = await sendPasswordResetEmail(user.value.email);
            if (success) {
                toast.add({
                    severity: "success",
                    summary: "Password reset email sent",
                    detail: "Please check your email for instructions.",
                });
            }
        } else {
            toast.add({
                severity: "error",
                summary: "Error",
                detail: "Unable to find user email for password reset.",
            });
        }
    } catch (error) {
        toast.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to send password reset email.",
        });

        throw error;
    }
};

watch(
    user,
    (newUser) => {
        if (newUser) {
            genForm.value.fullname = newUser.name;
            genForm.value.email = newUser.email;
        }
        displaySpinner.value = false;
    },
    { immediate: true }
);

watch(hintsEnabled, (val) => {
    hintsEnabledLocal.value = val;
});
</script>

<style lang="scss" scoped>
.password-reset,
.general-section,
.public-key {
    display: inline-block;
    width: 100%;
    .action-btn {
        text-align: right;
    }
}
.subtitle {
    font-style: italic;
}

.loading-container,
.error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    background-color: #f8f9fa;
    border-radius: 8px;
}

.error-container {
    border-left: 4px solid #f44336;
}

.general-section .form-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px;
}
.general-section .button-group {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
}

.hints-section {
    margin-top: 2rem;
    border-top: 1px solid var(--p-menubar-border-color);
    padding-top: 1rem;
}

.hints-controls {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.hints-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--p-button-secondary-background);
    padding: 0.5em 1em;
    border: 1px solid var(--p-menubar-border-color);
}

.api-keys-section {
    padding: 1rem 0;
}

.api-keys-section h3 {
    margin-top: 0;
    margin-bottom: 1rem;
}

.api-key-actions {
    margin: 1.5rem 0;
    display: flex;
    gap: 10px;
}

.mr-2 {
    margin-right: 0.5rem;
}

.roles-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.roles-header h3 {
    margin: 0;
}

.roles-table {
    margin-top: 1rem;
    width: 100%;
}

.empty-roles {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    color: #6c757d;
    font-style: italic;
}

.role-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.role-badge {
    padding: 3px 8px;
    font-size: 0.8rem;
    font-weight: bold;
    display: inline-block;
    white-space: nowrap;

    &.role-manager {
        background-color: var(--stekz-primary-background);
        color: var(--stekz-primary-color);
    }

    &.role-stek-manager {
        background-color: var(--stekz-secondary-background);
        color: var(--stekz-secondary-color);
    }

    &.role-editor {
        background-color: var(--stekz-tertiary-background);
        color: var(--stekz-tertiary-color);
    }

    &.role-viewer {
        background-color: var(--surface-background-mild);
        color: var(--text-color-secondary);
    }
}

.user-integrations-section {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #dee2e6;
}

.integrations-table {
    margin-top: 1rem;
}

.empty-integrations {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    color: #6c757d;
    display: flex;
    align-items: center;

    i.pi-info-circle {
        font-size: 1.2rem;
        margin-right: 0.75rem;
        color: var(--primary-color);
    }

    i.pi-cog {
        margin: 0 0.25rem;
    }
}

.org-name-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;

    span {
        flex: 1;
    }

    .config-link {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem;
        color: var(--text-color-secondary);
        border-radius: 4px;
        transition: all 0.2s ease;
        text-decoration: none;

        &:hover {
            color: var(--primary-color);
            background-color: var(--surface-hover);
        }

        i {
            font-size: 1rem;
        }
    }
}

.tab-link {
    text-decoration: none;
    color: inherit;
    display: block;
    width: 100%;
    height: 100%;

    &:hover {
        text-decoration: none;
        color: inherit;
    }
}
</style>
